import { useMemo, useState } from 'react';
import {
  MERGE_FIELD_GROUPS as MERGE_FIELD_GROUPS_RAW,
  MERGE_FIELD_GROUP_LABELS as MERGE_FIELD_GROUP_LABELS_RAW,
  mergeFieldsByGroup as mergeFieldsByGroupRaw,
  MERGE_FIELD_CATALOG as MERGE_FIELD_CATALOG_RAW,
  resolvableIn as resolvableInRaw,
} from '@platform/shared-types';
import type { MergeFieldDef, MergeFieldGroup, MergeContext } from '@platform/shared-types';
import { chipClassesForGroup, dotClassForGroup } from './mergeFieldColors';
import { FIELD_INSERT_MIME } from './mergeEditorActions';
import { INPUT_CLASS } from './ui';

/**
 * The grouped, searchable palette an author drags (or clicks) fields in
 * from. Reads the catalog defensively — see the note below — and degrades to
 * an empty palette rather than white-screening the page.
 *
 * `MERGE_FIELD_GROUPS`, `MERGE_FIELD_GROUP_LABELS` and `mergeFieldsByGroup`
 * are RUNTIME values from `@platform/shared-types`. A Vite dep pre-bundle
 * built before these exports existed hands back `undefined` for them — at
 * MODULE SCOPE for the first two, which would white-screen the whole app if
 * used unguarded (`undefined.map(...)`), not just this palette. The console
 * line names the actual fix: RESTART the dev server. Deleting
 * `node_modules/.vite` under a running server does nothing — Vite keeps the
 * optimised bundle in memory and rewrites the directory on its own schedule.
 */
const STALE_BUNDLE_MSG = (name: string) =>
  `@platform/shared-types exported no ${name} — a stale Vite pre-bundle. RESTART the dev server: deleting node_modules/.vite under a running server does nothing.`;

/** Guarded catalog constants, exported so `BlockEditor.tsx`'s `MergeFieldPicker`
 *  reuses the same defensively-loaded values instead of re-guarding them. */
export const MERGE_FIELD_GROUPS: readonly MergeFieldGroup[] = (() => {
  if (Array.isArray(MERGE_FIELD_GROUPS_RAW)) return MERGE_FIELD_GROUPS_RAW;
  console.error(STALE_BUNDLE_MSG('MERGE_FIELD_GROUPS'));
  return [];
})();

export const MERGE_FIELD_GROUP_LABELS: Readonly<Record<string, string>> = (() => {
  if (MERGE_FIELD_GROUP_LABELS_RAW && typeof MERGE_FIELD_GROUP_LABELS_RAW === 'object') {
    return MERGE_FIELD_GROUP_LABELS_RAW;
  }
  console.error(STALE_BUNDLE_MSG('MERGE_FIELD_GROUP_LABELS'));
  return {};
})();

/** Guarded call — exported so `BlockEditor.tsx`'s mergeField picker reads the
 *  same defensively-loaded catalog instead of importing the raw function a
 *  second time. */
export function safeMergeFieldsByGroup(): Record<MergeFieldGroup, MergeFieldDef[]> {
  if (typeof mergeFieldsByGroupRaw !== 'function') {
    console.error(STALE_BUNDLE_MSG('mergeFieldsByGroup'));
    return {} as Record<MergeFieldGroup, MergeFieldDef[]>;
  }
  try {
    return mergeFieldsByGroupRaw();
  } catch (e) {
    console.error('[MergeFieldPalette] mergeFieldsByGroup() threw:', e);
    return {} as Record<MergeFieldGroup, MergeFieldDef[]>;
  }
}

/** Guarded catalog array — same defensive pattern as the constants above. */
const MERGE_FIELD_CATALOG: readonly MergeFieldDef[] = (() => {
  if (Array.isArray(MERGE_FIELD_CATALOG_RAW)) return MERGE_FIELD_CATALOG_RAW as readonly MergeFieldDef[];
  console.error(STALE_BUNDLE_MSG('MERGE_FIELD_CATALOG'));
  return [];
})();

function safeResolvableIn(def: MergeFieldDef, ctx: MergeContext): boolean {
  if (typeof resolvableInRaw !== 'function') {
    console.error(STALE_BUNDLE_MSG('resolvableIn'));
    return false;
  }
  try {
    return resolvableInRaw(def, ctx);
  } catch (e) {
    console.error('[MergeFieldPalette] resolvableIn() threw:', e);
    return false;
  }
}

/**
 * The pure predicate, separated from the real catalog so it can be tested
 * against a synthetic `deprecated` fixture — no test against the real
 * catalog can prove this filter does anything while no catalog entry
 * actually sets `deprecated` (spec R5: fields are deprecated, never
 * deleted, so this WILL eventually matter against real data too).
 */
export function filterFields(defs: readonly MergeFieldDef[], ctx: MergeContext): MergeFieldDef[] {
  return defs.filter((d) => !d.deprecated && safeResolvableIn(d, ctx));
}

/**
 * What an author may pick, for THIS document's context. Aliases are never
 * catalog entries in their own right, so they can never appear here; a
 * `deprecated` field still resolves (already-seeded documents keep
 * rendering) but is never offered for new authoring — spec R5.
 */
export function visibleFields(ctx: MergeContext): MergeFieldDef[] {
  return filterFields(MERGE_FIELD_CATALOG, ctx);
}

/** The context a document that declares none is authored against. */
export const DEFAULT_MERGE_CONTEXT: MergeContext = 'deal_hardware';

/**
 * What an author may pick for a document declaring SEVERAL contexts — the
 * INTERSECTION, because the publish gate fails a field that is structural in
 * any one of them. Offering the union would hand the author fields guaranteed
 * to block their own publish.
 *
 * Exported so the palette and the tab strip that sits above it agree by
 * construction. A tab strip with its own idea of which groups exist is how the
 * five groups added on 2026-08-31 stayed invisible for a whole release: the
 * catalog had them, the palette had them, and a hand-written list of eight did
 * not.
 */
export function visibleFieldsForContexts(
  contexts?: readonly MergeContext[],
): MergeFieldDef[] {
  const active = contexts && contexts.length ? contexts : [DEFAULT_MERGE_CONTEXT];
  return MERGE_FIELD_CATALOG.filter(
    (d) => !d.deprecated && active.every((ctx) => safeResolvableIn(d, ctx)),
  );
}

function matches(def: MergeFieldDef, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return def.name.toLowerCase().includes(q) || def.label.toLowerCase().includes(q);
}

export interface MergeFieldPaletteProps {
  /** Click-to-insert. Drag-and-drop is handled by the entry itself via the
   *  `FIELD_INSERT_MIME` payload — `MergeTextEditor` reads it on drop. */
  onInsert: (name: string) => void;
  /**
   * Show ONE group only. Absent shows them all.
   *
   * An unknown group id renders an empty list rather than silently falling
   * back to everything: a tab that quietly shows the wrong contents is worse
   * than one that visibly shows none.
   */
  onlyGroup?: string;
  /**
   * Which document this palette is authoring for. Only fields resolvable in
   * this context are offered — see `visibleFields`. Defaults to
   * `deal_hardware`, the widest currently-populated context, matching the
   * publish gate's own default for a document that declares no `contexts`
   * (documents-service `lifecycle.ts`).
   */
  context?: MergeContext;
  /**
   * EVERY context the document declares. Takes precedence over `context`.
   *
   * A field is offered only when it resolves in ALL of them — the INTERSECTION,
   * not the union. The publish gate fails a field that is structural in ANY
   * declared context, so offering the union would hand the author fields that
   * are guaranteed to block their own publish. An empty array falls back to
   * `context`, matching a document that declares none.
   */
  contexts?: readonly MergeContext[];
  className?: string;
}

/** One draggable + clickable catalog entry. */
function PaletteEntry({ def, onInsert }: { def: MergeFieldDef; onInsert: (name: string) => void }) {
  return (
    <button
      type="button"
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData(FIELD_INSERT_MIME, def.name);
        e.dataTransfer.effectAllowed = 'copy';
      }}
      onClick={() => onInsert(def.name)}
      title={`{{${def.name}}} — e.g. "${def.example}"`}
      className={`flex min-h-[36px] w-full cursor-grab items-center gap-1.5 rounded-md px-2 py-1 text-left text-[12px] transition-colors active:cursor-grabbing ${chipClassesForGroup(def.group)} hover:brightness-95`}
    >
      <span className="min-w-0 flex-1 truncate">{def.label}</span>
      {def.source === 'manual' && (
        <span
          title="Supplied by a human at signing, not filled in automatically"
          className="shrink-0 rounded-full bg-black/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider"
        >
          manual
        </span>
      )}
    </button>
  );
}

/** Grouped, searchable palette of every merge field an author can drag or
 *  click into a document. */
export function MergeFieldPalette({
  onInsert, onlyGroup, context = 'deal_hardware', contexts, className = '',
}: MergeFieldPaletteProps) {
  const [query, setQuery] = useState('');
  // Joined so the memo re-runs on a genuine change of contexts rather than on
  // every render that rebuilds the array literal.
  const contextKey = (contexts && contexts.length ? contexts : [context]).join('|');
  const grouped = useMemo(() => {
    const byGroup = {} as Record<MergeFieldGroup, MergeFieldDef[]>;
    for (const group of MERGE_FIELD_GROUPS) byGroup[group] = [];
    for (const def of visibleFieldsForContexts(contextKey.split('|') as MergeContext[])) {
      byGroup[def.group].push(def);
    }
    return byGroup;
  }, [contextKey]);

  const visibleGroups = MERGE_FIELD_GROUPS
    .filter((group) => onlyGroup === undefined || group === onlyGroup)
    .map((group) => ({ group, defs: (grouped[group] ?? []).filter((d) => matches(d, query)) }))
    .filter((g) => g.defs.length > 0);

  return (
    <div className={`space-y-3 ${className}`}>
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search tags…"
        aria-label="Search tags"
        className={INPUT_CLASS}
      />

      {MERGE_FIELD_GROUPS.length === 0 && (
        <p className="text-[11.5px] text-red-300">
          No tag catalog loaded — restart the dev server (see console).
        </p>
      )}

      <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
        {visibleGroups.length === 0 && MERGE_FIELD_GROUPS.length > 0 && (
          <p className="text-[11.5px] text-stone-500">No fields match "{query}".</p>
        )}
        {visibleGroups.map(({ group, defs }) => (
          <div key={group}>
            <div className="mb-1.5 flex items-center gap-1.5">
              <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotClassForGroup(group)}`} aria-hidden="true" />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                {MERGE_FIELD_GROUP_LABELS[group] ?? group}
              </span>
            </div>
            <div className="space-y-1">
              {defs.map((def) => (
                <PaletteEntry key={def.name} def={def} onInsert={onInsert} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
